import { useSelector } from 'react-redux'
import { useQuery, ApolloError } from '@apollo/client'
import { pathOr } from 'ramda'
import { GlobalState } from 'types'
import getConfig, { isServer } from 'config/web'
import { RESOUCE_SET_BY_NAME } from 'queries/resource-set-by-name/query'
import RichTextType from 'models/types/ts/richTextType'

import { PlexForGoodTranslations, ContentfulResourceItem } from './types'
import { defaultTranslations } from './defaults'

const {
    contentful: { preview },
} = getConfig()

/**
 * Custom hook to fetch translations from Contentful
 * @param resourceSetName The name of the resource set in Contentful
 * @returns Translations object with merged default and Contentful values
 */
export const useTranslations = (
    resourceSetName = 'PlexForGood'
): {
    translations: PlexForGoodTranslations
    loading: boolean
    error: ApolloError | undefined
} => {
    const language = useSelector((state: GlobalState) => state.language)

    const { loading, error, data } = useQuery(RESOUCE_SET_BY_NAME, {
        variables: { name: resourceSetName, locale: language, preview },
        errorPolicy: 'ignore',
        fetchPolicy: isServer ? 'cache-and-network' : 'cache-first',
    })

    // Extract resources from Contentful data
    const resourceItems: ContentfulResourceItem[] = pathOr(
        [],
        ['resourceSetCollection', 'items', '0', 'resourcesCollection', 'items'],
        data
    )

    // Convert to dictionary
    const resources = resourceItems.reduce<Record<string, RichTextType>>(
        (acc, item) => {
            if (item && typeof item.key === 'string') {
                acc[item.key] = item.value
            }
            return acc
        },
        {}
    )

    // Merge default translations with Contentful translations
    const translations = Object.keys(defaultTranslations).reduce<
        Partial<PlexForGoodTranslations>
    >((acc, key) => {
        const typedKey = key as keyof PlexForGoodTranslations
        acc[typedKey] = resources[key] || defaultTranslations[typedKey]
        return acc
    }, {}) as PlexForGoodTranslations

    return { translations, loading, error }
}
